import type {
	CellContext,
	Column,
	ColumnDef,
	ColumnDefTemplate,
	HeaderContext,
	OnChangeFn,
	PaginationState,
	Row,
	RowData,
	RowSelectionState,
	SortingState,
	Table,
} from '@tanstack/react-table';
import type * as React from 'react';
import type { DateRangeOptions } from '../date-range-view/date-range-view-types';
import type { DateTimeOptions } from '../date-time-view/date-time-view.types';
import { type FilterState } from './components/action-bar/filter-menu';
import type { DataGridFilterOption, FilterOptionsState } from './components/action-bar/hooks/use-filters';
import type { DataGridTableActions } from './components/action-bar/table-actions';
import type { MIN_ROW_HEIGHT } from './data-grid.constants';
import type { APIMeta } from './hooks/use-api';
import type { DataGridState } from './hooks/use-data-grid';
import type { DataGridColumnPredefinedCellTypeConfig } from './util/cells';
import type { DataGridDateRangeCell } from './util/cells/daterange';
import { type FinCellOptions } from './util/cells/fin';
import type { IDataGridUrlDateRange, IDataGridUrlSyncOptions } from './util/url-sync/types';

declare module '@tanstack/react-table' {
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	interface ColumnMeta<TData extends RowData, TValue> {
		/**
		 * Set when a {@link DataGridColumnDef} declares an explicit `width`. The grid body uses this
		 * flag (instead of inspecting `minSize`) to decide whether to pin the cell width/maxWidth,
		 * so a real resize floor can still be expressed via `minSize` without affecting layout.
		 */
		hasExplicitWidth?: boolean;
		/**
		 * Serialized selection state attached to the selection column so the header cell can
		 * react to cross-page selection changes (see `columns/selection.tsx`).
		 */
		selectionState?: string;
	}
}

type ValueOf<T> = T[keyof T];

interface DataGridBaseColumn<T, K extends keyof T, C extends DataGridColumnType> {
	customCell?: ColumnDefTemplate<CellContext<T, unknown>>;
	customHeader?: ColumnDefTemplate<HeaderContext<T, unknown>>;
	finOptions?: C extends 'fin' ? FinCellOptions : never;
	filter?: {
		initial?: string[] | number[];
		api?: DataGridFilterAPIOptions;
		options?: DataGridFilterOption[];
		paramName?: string; // Optional API parameter name to use when filtering, if different from key
		search?: boolean; // Optional flag to enable search functionality for this filter
		transform?: (value: unknown) => unknown; // Optional transform function to convert cell value to filter value
	};
	header?: string;
	hidden?: boolean | ((filters: DataGridFilterOptionsState, activeFilters: Record<string, FilterState>) => boolean);
	key: string & K;
	parse?: (value: T[K], row: T, options: DataGridOptions) => DataGridColumnTypeConfig[C]['value'];
	resizable?: boolean;
	sortable?: boolean;
	width?: number;
	options?: C extends keyof DataGridColumnTypeOptions ? DataGridColumnTypeOptions[C] : never;
}

export type DataGridBaseColumnDef<T, C extends DataGridColumnType> = ValueOf<{
	[K in keyof T]: DataGridBaseColumn<T, K, C>;
}>;

export interface DataGridBaseRow {
	id?: number | string;
}

export interface DataGridSelectedRowsMetadata<T> {
	all: boolean;
	selectedRows: T[];
	excludedIds: (string | number)[];
	filters: Record<string, FilterState>;
	search?: string;
	totalRowsCount: number;
	selectedRowsCount: number;
}

export interface DataGridFilterAPIOptions {
	method: (...args: any[]) => any;
	params?: Record<string, unknown>;
	responseParser?: (response: any) => any[];
}

export interface DataGridColumnDefOptions extends DataGridOptions {
	finOptions?: FinCellOptions;
	parse?: (value: any, row: any, options: DataGridOptions) => any;
}

export type DataGridColumnTypeOptions = {
	daterange: DateRangeOptions;
	datetime: DateTimeOptions;
};

export type DurationFormatParts = Partial<{
	years: number;
	months: number;
	weeks: number;
	days: number;
	hours: number;
	minutes: number;
	seconds: number;
}>;

export type DurationFormatStyle = 'short' | 'long' | 'narrow';

export type DataGridColumnTypeConfig = {
	actions: { type: 'actions'; value: never };
	boolean: { type: 'boolean'; value: boolean };
	custom: { type: 'custom'; value: unknown }; // TODO: Not sure what this will do yet
	date: { type: 'date'; value: string | number | Date };
	'filter-only': { type: 'void'; value: never };
	number: { type: 'number'; value: number };
	score: { type: 'score'; value: { current: number; total: number } };
	text: { type: 'text'; value: string };
} & DataGridColumnPredefinedCellTypeConfig;

export type DataGridColumnType = keyof DataGridColumnTypeConfig;
// NOTE: If column types are not simple they need to be excluded here.
export type DataGridSimpleColumnTypes = Exclude<DataGridColumnType, 'actions'>;

/**
 * Union type that represents all possible column definitions for the DataGrid component.
 */
export type DataGridColumnDef<T> =
	| {
			[K in DataGridSimpleColumnTypes]: DataGridSimpleTypeColumnDef<T, K>;
	  }[DataGridSimpleColumnTypes]
	| DataGridActionColumnDef<T>
	| DataGridFilterOnlyColumnDef<T>;

export interface DataGridActionDisabledReason {
	message: string;
}

export type TRowSelectionDisabledResult = boolean | DataGridActionDisabledReason;

export interface IDataGridRowAction<T> {
	icon?: React.ComponentType<{ className?: string }>;
	label: string | ((row: T) => string);
	onClick: (row: T) => void;
	hidden?: ((row: T) => boolean) | boolean;
	isUsable?: (row: T) => boolean | DataGridActionDisabledReason;
	dataTestId?: string;
	/**
	 * When true, this action renders as an inline button instead of inside the "more" (kebab) menu.
	 * Actions without this flag fall into the menu, so buttons and a menu list can be mixed in the
	 * same column. The column-level `inline` flag forces every action inline regardless of this value.
	 * @default false
	 */
	inline?: boolean;
}

export type DataGridActionColumnDef<T> = Omit<DataGridBaseColumnDef<T, DataGridSimpleColumnTypes>, 'key'> & {
	type: 'actions';
	key: 'actions';
	actions: Array<IDataGridRowAction<T>>;
	/**
	 * When true, every row action renders as an inline button instead of the default "more" (kebab) menu.
	 * To mix inline buttons with a menu list, leave this unset and mark individual actions with
	 * `inline: true` on {@link IDataGridRowAction}; the remaining actions fall into the menu.
	 * @default false
	 */
	inline?: boolean;
	/**
	 * Function to determine if all actions should be available for a row
	 * @param row - The row data
	 * @returns true if actions are available, or a reason object if not
	 *
	 * @example
	 * // Disable all actions for inactive items
	 * actionsUsability: (row) => row.status === 'active'
	 *   ? true
	 *   : { message: 'Actions not available for inactive items' }
	 */
	actionsUsability?: (row: T) => boolean | DataGridActionDisabledReason;
};

export type DataGridFilterOnlyColumnDef<T> = Omit<DataGridBaseColumnDef<T, DataGridSimpleColumnTypes>, 'key'> & {
	type: 'filter-only';
	key: string;
};

export type DataGridSimpleTypeColumnDef<T, K extends DataGridColumnType> = DataGridBaseColumnDef<T, K> & {
	format?: (value: DataGridColumnTypeConfig[K]['value']) => React.ReactNode;
	type: K;
};

export type DataGridRowsHeight = keyof typeof MIN_ROW_HEIGHT;

export interface DataGridProps<T extends DataGridBaseRow> {
	// TODO: CD type needs to be scoped
	actionBarProps?: DataGridActionBarProps<T>;
	apiArgs?: any[];
	apiMethod?: (...args: any[]) => any; // TODO: Improve return type ideally to be: Promise<T[]>
	apiResponseParser?: (response: any) => any[]; // TODO: Improve return type ideally to be: T[]
	columns: DataGridColumnDef<T>[];
	data?: T[];
	defaultSortColumn?: string;
	defaultSortDirection?: OrderByEnum;
	emptyState?: string;
	handleSort?: (isAsc: boolean, orderBy: string) => void;
	meta?: APIMeta;
	onError?: (error: Error) => void;
	onLoadingChange?: (isLoading: boolean) => void;
	onPaginationChange?: (newPagination: PaginationState) => void;
	onRefetchChange?: (refetch: () => Promise<void>) => void;
	onRowClick?: (row: T) => void | null;
	onSelectRows?: (rows: DataGridSelectedRowsMetadata<T>) => void;
	onSelectionChange?: (selectedRows: T[]) => void;
	onSortingChange?: (newSorting: SortingState) => void;
	onStateChange?: (newState: DataGridState<T>) => void;
	withSelectAll?: boolean;
	/**
	 * Marks a row as disabled: muted text, default cursor, blocked `onRowClick`, and a disabled checkbox.
	 * When a function, return `false` to allow the row, `true` to disable with no tooltip,
	 * or `{ message: string }` (same shape as {@link DataGridActionDisabledReason}) to disable with one tooltip on the whole row.
	 * When `boolean`, all rows share that state with no per-row tooltip.
	 * When omitted, no rows are disabled.
	 */
	disableRowSelection?: boolean | ((row: T) => TRowSelectionDisabledResult);
	/**
	 * Row IDs (matching `uniqueField`) that should be pre-selected when data first loads.
	 * The selection is seeded only once per data load and the rows behave as normal selections
	 * afterwards — combine with `disableRowSelection` to render them as checked + disabled (which greys the whole row and blocks clicking).
	 */
	initialSelectedRowIds?: (string | number)[];
	options?: DataGridOptions;
	pageSize?: number;
	rowCount?: number;
	rowSelection?: 'single' | 'multiple';
	strings?: DataGridStrings;
	filtersFetcher?: () => Promise<DataGridFilterFetcherResponse>;
	uniqueField?: keyof T;
	/**
	 * Row height as a named token (`'md'` | `'lg'`) or an explicit pixel value. Rows render at
	 * exactly this height (taller content is clipped), so the grid can compute how many rows fit
	 * before its first fetch and the count never changes between pages. Provide a pixel value when
	 * a grid's cells (e.g. a media thumbnail) are taller than the token floor.
	 */
	rowsHeight?: DataGridRowsHeight | number;
	/**
	 * Opt-in query-string sync for page, search, sort, filters, and column visibility. Omit it and
	 * the grid behaves exactly as it does without URL sync.
	 *
	 * The grid reads the URL once when it mounts, so it must only be rendered once the consumer
	 * knows which data set it represents (see {@link IDataGridUrlSyncOptions.contextKey}).
	 */
	urlSync?: IDataGridUrlSyncOptions;
	/**
	 * Parent-owned date range written to `dateFrom` / `dateTo` when {@link urlSync} is on.
	 * Omit both this and {@link defaultDateRange} on grids that have no range picker.
	 */
	dateRange?: IDataGridUrlDateRange;
	defaultDateRange?: IDataGridUrlDateRange;
	/**
	 * Called when URL sync hydrates or restores a date range (mount and Back/Forward).
	 * Values are calendar dates (`YYYY-MM-DD`).
	 */
	onDateRangeChange?: (range: IDataGridUrlDateRange) => void;
}

/**
 * DataGridOptions
 * @property {string} locale - The locale to use for formatting dates and numbers.
 * @property {string} timezone - The timezone to use for formatting dates.
 * @property {FinCellOptions} finOptions - Options for financial cell formatting, applied to all `fin`-type columns.
 */
export interface DataGridOptions {
	locale?: string; // TODO: Is there a list for this?
	timezone?: string; // TODO: Is there a list for this?
	/**
	 * If true, the data grid will shrink to fit the rows.
	 * This is useful when the data grid is inside a flex container and there are few rows.
	 * @default false
	 */
	shrinkToFit?: boolean;
	finOptions?: FinCellOptions;
}

export interface DataGridStrings {
	// General messages
	errorMessage?: string;
	loadingMessage?: string;
	noColumnsMessage?: string;
	noDataMessage?: string;
	subject: string;

	// Action Bar strings
	actionBar?: {
		searchPlaceholder?: string;
	};

	// Column visibility menu strings
	columnVisibility?: {
		buttonLabel?: string;
		selectAll?: string;
	};

	// Filter chip strings
	filterChips?: {
		remove?: string; // Aria label for remove button
	};

	// Filter menu strings
	filters?: {
		buttonLabel?: string;
		retry?: string;
		searchNoDataMessage?: string;
		searchPlaceholder?: string;
		selectAll?: string;
	};

	// Footer strings
	footer?: {
		showing?: string;
		of?: string;
		selected?: string;
		itemsPerPage?: string;
		// For the pattern "Showing 1 to 10 of 100 items"
		showingItemsRange?: {
			text: string;
			// Supports patterns like "Showing {start} to {end} of {total} {subject}"
			variables: {
				start: string;
				end: string;
				total: string;
				subject: string;
			};
		};
	};
}

export interface DataGridActionBarProps<T> {
	customActions?: React.ReactNode;
	/** Rendered at the start of the action bar, before the search input (e.g. a segmented control). */
	leadingSlot?: React.ReactNode;
	hasCustomFilters?: boolean;
	onClearFilters?: () => void;
	searchDisabled?: boolean;
	searchWidth?: number | string;
	showColumnVisibility?: boolean;
	showFilterGroupSearch?: boolean;
	showFilterGroupSelectAll?: boolean;
	showFilterMenu?: boolean;
	showSearch?: boolean;
	showTableActions?: boolean;
	tableActions?: DataGridTableActions<T>['actions'];
	dateRangeFilter?: React.ReactNode;
}

export type DataGridRow<T> = T & {
	__selectedState?: boolean;
	__isDisabled?: boolean;
	__disabledTooltip?: string;
};

interface DataTableProps<TData, TValue> {
	strings: {
		showingCount: string;
	};
	className?: string;
	columns: ColumnDef<TData, TValue>[];
	data: TData[];
	onSelectRows?: (rows: TData[]) => void;
	onSelectAllRows?: () => void;
	onSort?: (column: ColumnDef<TData, TValue>) => void;
	onHideColumn?: (column: ColumnDef<TData, TValue>) => void;
	onRowClick?: (row: TData) => void;
	isLoading?: boolean;
	ssr?: boolean;
	pageSize?: number;
	rowCount?: number;
	pagination?: PaginationState;
	onPaginationChange?: OnChangeFn<PaginationState>;
	onSortingChange?: OnChangeFn<SortingState>;
	onRowSelectionChange?: OnChangeFn<RowSelectionState>;
	emptyState?: React.ReactNode;
}

interface DataTableColumnHeaderProps<TData, TValue> extends React.HTMLAttributes<HTMLDivElement> {
	className?: string;
	column: Column<TData, TValue>;
	table?: Table<TData>;
	title: string;
	type?: HeaderType;
	initialSortDirection?: ESortDirection;
}

interface OverflowMenuItems {
	text: string;
	onMenuSelect: (row: Row<any>) => void;
}

export interface IRowSelectionDisabledResolved {
	isDisabled: boolean;
	tooltipMessage?: string;
}

enum ESortDirection {
	ASC = 'asc',
	DESC = 'desc',
}

enum HeaderType {
	DEFAULT = 'default',
	SELECT = 'select',
}

export type DataGridFilterFetcherResponse = Record<string, DataGridFilterOption[]>;

export type DataGridFilterOptionsState = Record<string, FilterOptionsState>;

export { ESortDirection, HeaderType };
export type { DataTableProps, DataTableColumnHeaderProps, OverflowMenuItems, DataGridDateRangeCell };

/*
Backoffice Types
*/
export enum OrderByEnum {
	ASC = 'ASC',
	DESC = 'DESC',
}

export interface SortTable<T> {
	order: OrderByEnum;
	orderBy: T;
}

export enum TableActionKey {
	create = 'create',
	edit = 'edit',
	delete = 'delete',
	email = 'email',
}
